import {
  DEFAULT_SEARCH_EXPRESSION_OPERATOR,
} from "./constants.js";
import { isPlainObject } from "./shared.js";
import {
  normalizeSearchTerm,
  normalizeSelectedSearchTerms,
  searchTermKey,
} from "./terms.js";

export function normalizeSearchExpressionOperator(value) {
  return String(value ?? "").trim().toLowerCase() === "and"
    ? "and"
    : DEFAULT_SEARCH_EXPRESSION_OPERATOR;
}

export function normalizeSearchExpressionNegated(value) {
  return value === true;
}

export function cloneSearchExpressionNode(node) {
  if (!isPlainObject(node)) {
    return null;
  }
  if (node.type === "term") {
    const cloned = {
      type: "term",
      term: normalizeSearchTerm(node.term),
    };
    if (normalizeSearchExpressionNegated(node.negated)) {
      cloned.negated = true;
    }
    return cloned;
  }
  if (node.type === "group") {
    const cloned = {
      type: "group",
      operator: normalizeSearchExpressionOperator(node.operator),
      children: (Array.isArray(node.children) ? node.children : [])
        .map((child) => cloneSearchExpressionNode(child))
        .filter(Boolean),
    };
    if (normalizeSearchExpressionNegated(node.negated)) {
      cloned.negated = true;
    }
    return cloned;
  }
  if (node.type === "container") {
    const expression = cloneSearchExpressionNode(node.expression);
    if (!expression) {
      return null;
    }
    const cloned = {
      type: "container",
      expression,
    };
    if (normalizeSearchExpressionNegated(node.negated)) {
      cloned.negated = true;
    }
    return cloned;
  }
  return null;
}

function mergeSearchExpressionNodeNegation(node, negated) {
  const clonedNode = cloneSearchExpressionNode(node);
  if (!clonedNode) {
    return null;
  }
  const nextNegated = normalizeSearchExpressionNegated(clonedNode.negated)
    !== normalizeSearchExpressionNegated(negated);
  if (nextNegated) {
    clonedNode.negated = true;
  } else {
    delete clonedNode.negated;
  }
  return clonedNode;
}

export function compactSearchExpressionNode(node, { isRoot = false } = {}) {
  const clonedNode = cloneSearchExpressionNode(node);
  if (!clonedNode) {
    return null;
  }
  if (clonedNode.type === "term") {
    return clonedNode.term ? clonedNode : null;
  }
  if (clonedNode.type === "container") {
    const expression = compactSearchExpressionNode(clonedNode.expression);
    if (!expression) {
      return null;
    }
    const compacted = { type: "container", expression };
    if (normalizeSearchExpressionNegated(clonedNode.negated)) {
      compacted.negated = true;
    }
    return compacted;
  }
  const operator = normalizeSearchExpressionOperator(clonedNode.operator);
  const children = clonedNode.children
    .map((child) => compactSearchExpressionNode(child))
    .filter(Boolean);
  if (!isRoot && children.length === 0) {
    return null;
  }
  if (!isRoot && children.length === 1) {
    return mergeSearchExpressionNodeNegation(children[0], clonedNode.negated);
  }
  const compacted = {
    type: "group",
    operator,
    children,
  };
  if (normalizeSearchExpressionNegated(clonedNode.negated)) {
    compacted.negated = true;
  }
  return compacted;
}

export function buildSearchExpressionFromSelectedTerms(selectedTerms, options = {}) {
  const operator = normalizeSearchExpressionOperator(options.operator);
  return {
    type: "group",
    operator,
    children: normalizeSelectedSearchTerms(selectedTerms).map((term) => ({
      type: "term",
      term,
    })),
  };
}

export function searchExpressionNodeKey(node) {
  if (!isPlainObject(node)) {
    return "";
  }
  const negationPrefix = normalizeSearchExpressionNegated(node.negated) ? "not:" : "";
  if (node.type === "term") {
    return `term:${negationPrefix}${searchTermKey(node.term)}`;
  }
  if (node.type === "group") {
    return `group:${negationPrefix}${normalizeSearchExpressionOperator(node.operator)}:${(
      Array.isArray(node.children) ? node.children : []
    )
      .map((child) => searchExpressionNodeKey(child))
      .filter(Boolean)
      .join("|")}`;
  }
  if (node.type === "container") {
    return `container:${negationPrefix}${searchExpressionNodeKey(node.expression)}`;
  }
  return "";
}

export function searchExpressionNodeHasPatchBound(node) {
  if (!isPlainObject(node)) {
    return false;
  }
  if (node.type === "term") {
    return node.term?.kind === "patch-bound";
  }
  if (node.type === "container") {
    return searchExpressionNodeHasPatchBound(node.expression);
  }
  return (Array.isArray(node.children) ? node.children : []).some((child) =>
    searchExpressionNodeHasPatchBound(child)
  );
}

function rememberSearchExpressionOrigin(originByNode, node, originPath) {
  if (originByNode && node && originPath) {
    originByNode.set(node, originPath);
  }
  return node;
}

function normalizeSearchExpressionNode(raw, originPath = "root", originByNode = null) {
  if (!isPlainObject(raw)) {
    const term = normalizeSearchTerm(raw);
    return term
      ? rememberSearchExpressionOrigin(originByNode, { type: "term", term }, originPath)
      : null;
  }

  const type = String(raw.type ?? raw.nodeType ?? "").trim().toLowerCase();
  if (type === "term") {
    const term = normalizeSearchTerm(raw.term ?? raw.value ?? raw.searchTerm);
    if (!term) {
      return null;
    }
    const normalized = { type: "term", term };
    if (normalizeSearchExpressionNegated(raw.negated ?? raw.not ?? raw.inverted)) {
      normalized.negated = true;
    }
    return rememberSearchExpressionOrigin(originByNode, normalized, originPath);
  }

  if (type === "container") {
    const expression = normalizeSearchExpressionNode(
      raw.expression,
      `${originPath}.0`,
      originByNode,
    );
    if (!expression) {
      return null;
    }
    const normalized = { type: "container", expression };
    if (normalizeSearchExpressionNegated(raw.negated ?? raw.not ?? raw.inverted)) {
      normalized.negated = true;
    }
    return rememberSearchExpressionOrigin(originByNode, normalized, originPath);
  }

  const childValues =
    Array.isArray(raw.children) ? raw.children
      : Array.isArray(raw.items) ? raw.items
        : Array.isArray(raw.nodes) ? raw.nodes
          : null;
  if (type === "group" || childValues) {
    const children = [];
    const seen = new Set();
    for (const [childIndex, childValue] of (
      Array.isArray(childValues) ? childValues : []
    ).entries()) {
      const child = normalizeSearchExpressionNode(
        childValue,
        `${originPath}.${childIndex}`,
        originByNode,
      );
      if (!child) {
        continue;
      }
      const key = searchExpressionNodeKey(child);
      if (!key || seen.has(key)) {
        continue;
      }
      seen.add(key);
      children.push(child);
    }
    const normalized = {
      type: "group",
      operator: normalizeSearchExpressionOperator(raw.operator ?? raw.op),
      children,
    };
    if (normalizeSearchExpressionNegated(raw.negated ?? raw.not ?? raw.inverted)) {
      normalized.negated = true;
    }
    return rememberSearchExpressionOrigin(originByNode, normalized, originPath);
  }

  const term = normalizeSearchTerm(raw);
  if (!term) {
    return null;
  }
  const normalized = { type: "term", term };
  if (normalizeSearchExpressionNegated(raw.negated ?? raw.not ?? raw.inverted)) {
    normalized.negated = true;
  }
  return rememberSearchExpressionOrigin(originByNode, normalized, originPath);
}

function normalizeSearchExpressionResult(value, includeOrigins) {
  if (value == null) {
    return { expression: null, origins: includeOrigins ? new Map() : null };
  }
  const originByNode = includeOrigins ? new WeakMap() : null;
  const node = normalizeSearchExpressionNode(value, "root", originByNode);
  if (!node) {
    return { expression: null, origins: includeOrigins ? new Map() : null };
  }
  const expression = node.type === "group"
    ? node
    : {
        type: "group",
        operator: DEFAULT_SEARCH_EXPRESSION_OPERATOR,
        children: [node],
      };
  if (!includeOrigins) {
    return { expression, origins: null };
  }
  const origins = new Map();
  const visit = (current, path) => {
    const originPath = originByNode.get(current);
    if (originPath) {
      origins.set(path, originPath);
    }
    if (current?.type === "group") {
      current.children.forEach((child, index) => visit(child, `${path}.${index}`));
    } else if (current?.type === "container") {
      visit(current.expression, `${path}.0`);
    }
  };
  visit(expression, "root");
  return { expression, origins };
}

export function normalizeSearchExpressionWithOrigins(value) {
  return normalizeSearchExpressionResult(value, true);
}

export function normalizeSearchExpression(value) {
  return normalizeSearchExpressionResult(value, false).expression;
}

export function coerceSearchExpression(expression) {
  return normalizeSearchExpression(expression) || buildSearchExpressionFromSelectedTerms([]);
}

export function selectedSearchTermsFromExpression(expression) {
  const normalizedExpression = coerceSearchExpression(expression);
  const terms = [];
  const seen = new Set();
  const visit = (node) => {
    if (!isPlainObject(node)) {
      return;
    }
    if (node.type === "term") {
      const term = normalizeSearchTerm(node.term);
      const key = searchTermKey(term);
      if (!term || !key || seen.has(key)) {
        return;
      }
      seen.add(key);
      terms.push(term);
      return;
    }
    if (node.type === "container") {
      visit(node.expression);
      return;
    }
    for (const child of Array.isArray(node.children) ? node.children : []) {
      visit(child);
    }
  };
  visit(normalizedExpression);
  return terms;
}

// A metadata contradiction is the empty fishing set; unlike an empty group it
// stays false on the native boundary and composes correctly beneath NOT/OR.
export function emptyFishingSelection(negated = false) {
  return { type: "group", operator: "and", children: [
    { type: "term", term: { kind: "status", status: "favourite" } },
    { type: "term", term: { kind: "status", status: "favourite" }, negated: true },
  ], ...(negated ? { negated: true } : {}) };
}
